import type { ExperimentalMutations } from '../__types__/ExperimentalMutations';
import { MediaType } from '../MediaType';
import { safeResponseJSON } from '../safeJSONParse';
import { throwErrorWithResponse } from '../throwErrorWithResponse';

export const experimentalPublishEditorContent: ExperimentalMutations['experimentalPublishEditorContent'] =
	(
		_,
		{
			contentId,
			contentType,
			title,
			spaceKey,
			content,
			version,
			message,
			ncsStepVersion,
			suppressNotifyWatchers,
			schedulePublishDate,
			ancestors,
			moveRequest,
			restrictions,
			isBlueprintCreate,
		},
		{ network: { BadStatusError, cfetch, getTraceIdFromResponse } },
	) => {
		let history;
		if (contentType === 'blogpost') {
			history = { createdDate: new Date().toISOString() };
		}

		const body = {
			id: contentId,
			type: contentType,
			title,
			space: { key: spaceKey },
			body: {
				atlas_doc_format: {
					value: content,
					representation: 'atlas_doc_format',
					content: {
						id: contentId,
					},
				},
			},
			status: 'current',
			version: {
				number: version,
				minorEdit: suppressNotifyWatchers,
				message,
				...(ncsStepVersion !== undefined && { ncsStepVersion }),
			},
			extensions: {
				'update-trigger': 'EDIT_PAGE',
				moveRequest: Object.keys(moveRequest ?? {}).length === 0 ? undefined : moveRequest,
			},
			history,
			schedulePublishDate,
			ancestors,
			// Insiead of sending restrictions: null, do not send restrictions at all
			restrictions: restrictions ?? undefined,
		};

		const resource = isBlueprintCreate
			? `/wiki/rest/api/content/blueprint/instance/${contentId}?status=draft`
			: `/wiki/rest/api/content/${contentId}/?status=draft&action=publish`;

		return cfetch(resource, {
			method: 'PUT',
			headers: {
				Accept: MediaType.JSON,
				'Content-Type': MediaType.JSON,
			},
			body: JSON.stringify(body),
		}).then(
			async (response) => {
				const json = await response
					.json()
					.catch((error) => throwErrorWithResponse(error, response));
				return {
					status: response.status,
					success: true,
					createdDate: json?.history?.createdDate,
					traceId: getTraceIdFromResponse(response) ?? '',
					space: {
						name: json?.space?.name,
					},
				};
			},
			async (error) => {
				if (BadStatusError.isBadStatusError(error)) {
					const { response } = error;
					const json = await safeResponseJSON(response);
					return {
						status: response.status,
						success: false,
						message: json?.message,
						traceId: getTraceIdFromResponse(response) ?? '',
						space: {
							name: '',
						},
					};
				}
				throw error;
			},
		);
	};
